
import { of as observableOf, Subject } from 'rxjs'

import { mdiFeather } from '@mdi/js'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import EmptyView from 'components/EmptyView'
import { PlannedPost } from 'components/Posts'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import SimpleLoader from 'components/SimpleLoader'
import { PlannedPost as IPlannedPost, PendingPost } from 'interfaces'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { BulkPostActionsProps } from 'routes/publishing/components/BulkActionsMenus/BulkPostActions'
import CopyPostsPopup from 'routes/publishing/components/CopyPostsPopup'
import { ERROR_NO_PROFILES_SUBTITLE, ERROR_NO_PROFILES_TITLE, PostErrorView } from 'routes/publishing/components/PostErrorView'
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators'
import { getDerivedStateFromPost, mergeState, resetComposer } from 'services/compose'
import { connectedDestinationsSelector } from 'services/destinations'
import { initPlans } from 'services/plan/actions'
import { setPlannedPostsView } from 'services/post'
import { bulkDeletePosts, fetchPendingPosts, shufflePosts, updatePostRecycle } from 'services/post/actions'
import {
  emptySlotsVisibleSelector,
  newPostSelector,
  plannedPostsLayoutSelector,
  selectedProfilesFetchIdsSelector,
  selectedProfilesSelector
} from 'services/post/selectors'
import { publishPosts } from 'services/post/shareNow/actions'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_POST_COPY, FEATURE_POST_RECYCLE, FEATURE_POST_SHUFFLE } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { addPosts, deleteMultiplePosts, setPosts, updateRecyclePost } from './actions'
import PlannedPostsList, { PostGroupData } from './components/PlannedPostsList'
import PostPreviewWithInfo from './components/PostPreviewWithInfo'
import { PostsGridView } from './components/PostsGridView'
import PublishingTopBar from './components/PublishingTopBar'
import styles from './PostViews.pcss'
import { PostsAction, postsReducer } from './reducer'
import { ContentType } from 'shared'

export function PendingPostsManager() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const selectedProfilesIds = useSelector(selectedProfilesSelector)
  const selectedProfilesFetchIds = useSelector(selectedProfilesFetchIdsSelector)
  const newPost = useSelector(newPostSelector)
  const connectedProfiles = useSelector(connectedDestinationsSelector)
  const emptySlotsVisible = useSelector(emptySlotsVisibleSelector)
  const activeView = useSelector(plannedPostsLayoutSelector)
  const [posts, postsDispatch] = useReducer<PendingPost[], [PostsAction]>(postsReducer, [])
  const postsRef = useRef<PendingPost[]>(posts)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [totalPostsCount, setTotalPostsCount] = useState(0)
  const [loadingOverlayActive, setLoadingOverlayActive] = useState(false)
  const [selectedPostsIds, setSelectedPostsIds] = useState<Record<string, boolean> | 'all'>({})
  const [postInMediaPreviewDialog, setPostInMediaPreviewDialog] = useState<IPlannedPost | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [search, setSearch] = useState<{ query: string, types: ContentType[], searchInSummary: boolean }>(
    { query: '', types: POST_TYPES, searchInSummary: true }
  )
  const [withPreview, setWithPreview] = useState(false)
  const [postInPreview, setPostInPreview] = useState<IPlannedPost | null>(null)
  const [postsToCopy, setPostsToCopy] = useState<IPlannedPost[]>([])

  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)
  const fetch$ = useRef<Subject<{
    ids: string[], page: number, query?: string, types?: ContentType[], searchInSummary: boolean
  }>>(undefined)
  const delete$ = useRef<Subject<{ postIds: string[], ppids: string[], all: boolean }>>(undefined)
  const share$ = useRef<Subject<{
    postIds: string[], ppids: string[], all: boolean, query?: string, types?: ContentType[]
  }>>(undefined)
  const shuffle$ = useRef<Subject<{ postIds: string[], ppids: string[], all?: boolean }>>(undefined)
  const recycle$ = useRef<Subject<{ postId: string, value: boolean | Date | number }>>(undefined)
  const selectionRef = useRef<{ postIds: string[], profileIds: string[], ppids: string[], all: boolean }>(undefined)

  const refreshPosts = useCallback(() => {
    setRefreshKey(current => current + 1)
  }, [])

  useEffect(() => {
    if (activeView === 'calendar') {
      dispatch(setPlannedPostsView('list'))
    }
  }, [activeView, dispatch])

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  useEffect(() => {
    dispatch(initPlans())
  }, [dispatch])

  const nonEmptyPosts = useMemo(() => {
    return posts.filter(p => !p.isEmpty)
  }, [posts])

  useEffect(() => {
    // EXPL: keep latest posts in a ref so item callbacks stay referentially stable
    // across posts updates and don't force every list item to re-render
    postsRef.current = posts
  }, [posts])

  useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(tap(({ page }) => {
        setLoading(true)
        setPage(page)
      })).pipe(
        switchMap(({ ids, page, query, types, searchInSummary }) => dispatch(fetchPendingPosts(page, ids, query, types, searchInSummary))
          .pipe(
            catchError(() => {
              dispatch(message('Error fetching posts! Please refresh.', 'error'))
              return observableOf([])
            })
          )))
      .subscribe((response: { posts: PendingPost[], page: number, totalPostsCount: number }) => {
        setLoading(false)
        setHasNextPage(response.posts.length > 0)
        setTotalPostsCount(response.totalPostsCount)
        if (response.page === 0) {
          postsDispatch(setPosts(response.posts))
        } else {
          postsDispatch(addPosts(response.posts))
        }
        setLoadingOverlayActive(false)
      })

    return () => fetch$.current?.unsubscribe()
  }, [dispatch])

  // Check for newly created posts while posts page is active. Reload posts to fetch the new records
  useEffect(() => {
    if (newPost || location.state?.refresh) {
      setRefreshKey(current => current + 1)
    }
  }, [newPost, location.state?.refresh])

  // Clear posts when profile selection changes
  useEffect(() => {
    postsDispatch(setPosts([]))
  }, [selectedProfilesFetchIds])

  // Fetch first page of posts when profile selection changes or posts refresh is requested
  useEffect(() => {
    if (selectedProfilesFetchIds.length > 0) {
      fetch$.current?.next({
        page: 0,
        ids: selectedProfilesFetchIds.split(','),
        query: search.query,
        types: search.types,
        searchInSummary: search.searchInSummary
      })
    }
  }, [selectedProfilesFetchIds, refreshKey, search.query, search.types, search.searchInSummary])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all }) => {
          return dispatch(bulkDeletePosts(postIds, ppids, 'pending', all, undefined, undefined, undefined, undefined, refreshPosts))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'post.notifications.delete-posts-error' }, { error: error.message }), 'error'))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          postsDispatch(deleteMultiplePosts(response.ids))
          setRefreshKey(current => current + 1)
        } else {
          setLoadingOverlayActive(false)
        }
      })

    return () => delete$.current?.unsubscribe()
  }, [dispatch, intl, refreshPosts])

  useEffect(() => {
    share$.current = new Subject()
    share$.current
      .pipe(
        tap(() => {
          setLoadingOverlayActive(true)
        }),
        mergeMap(({ ppids, postIds, all, query, types }) => {
          return dispatch(publishPosts(postIds, ppids, 'pending', all, undefined, query, types))
            .pipe(catchError((error: Error) => {
              dispatch(message(intl.formatMessage({ id: 'notifications.error-w-message' }, { error: error.message })))
              return observableOf({ error })
            }))
        })
      )
      .subscribe((response: any) => {
        if (!response.error) {
          setSelectedPostsIds({})
          dispatch(message(intl.formatMessage({ id: 'notifications.post-posted' }), 'success'))
          setRefreshKey(current => current + 1)
        } else {
          setLoadingOverlayActive(false)
        }
      })

    return () => share$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    shuffle$.current = new Subject()
    shuffle$.current.pipe(
      tap(() => {
        setLoadingOverlayActive(true)
        dispatch(message(intl.formatMessage({ id: 'post.notifications.shuffling-posts' })))
      }),
      mergeMap(({ postIds, ppids, all }) => dispatch(shufflePosts(postIds, ppids, 'pending', all)).pipe(catchError(() => {
        return observableOf({ error: true })
      })))
    )
      .subscribe(response => {
        if (!response.error) {
          setRefreshKey(current => current + 1)
        } else {
          setLoadingOverlayActive(false)
        }
      })

    return () => shuffle$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    recycle$.current = new Subject()
    recycle$.current
      .pipe(
        mergeMap(({ postId, value }) => dispatch(updatePostRecycle(postId, value))
          .pipe(catchError((e) => {
            const msg = e.response?.error.message || intl.formatMessage({ id: 'errors.generic' })
            dispatch(message(msg, 'error'))
            return observableOf({ error: true })
          }))
        )
      )
      .subscribe(response => {
        if (!response.error) {
          setSelectedPostsIds({})
          postsDispatch(updateRecyclePost(response.postId, response.value))
        }
      })

    return () => recycle$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    const profileIds = selectedProfilesFetchIds.split(',')
    const ppids = Object.values(connectedProfiles).filter(p => profileIds.includes(p.idNew)).map(p => p.ppid)
    if (selectedPostsIds === 'all') {
      selectionRef.current = {
        all: true,
        postIds: posts.map(p => p.id),
        profileIds,
        ppids
      }
    } else {
      selectionRef.current = {
        all: false,
        profileIds,
        ppids,
        postIds: Object.keys(selectedPostsIds).filter(id => selectedPostsIds[id])
      }
    }
  }, [selectedPostsIds, posts, selectedProfilesFetchIds, connectedProfiles])

  useEffect(() => {
    if (withPreview) {
      const firstNonEmptyPost = posts.find(p => !p.isEmpty)
      setPostInPreview(current => {
        if (!current && firstNonEmptyPost) {
          return firstNonEmptyPost
        }
        if (!firstNonEmptyPost) {
          return null
        }
        return posts.find(p => p.id === current?.id) || null
      })
    } else {
      setPostInPreview(null)
    }
  }, [withPreview, posts])

  const onTogglePreview = () => {
    setWithPreview(current => !current)
  }

  const onPostClick = useCallback((post: IPlannedPost) => {
    setWithPreview(true)
    setPostInPreview(post)
  }, [])

  const toggleAllPostsSelected = useCallback(() => {
    setSelectedPostsIds(current => current === 'all' ? {} : 'all')
  }, [])

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading) {
      fetch$.current?.next({ page: page + 1, ids: selectedProfilesFetchIds.split(','), ...search })
    }
  }, [page, selectedProfilesFetchIds, hasNextPage, loading, search])

  const onDeletePost = useCallback((id: string) => {
    delete$.current?.next({
      postIds: [id],
      ppids: selectionRef.current?.ppids || [],
      all: false
    })
  }, [])

  const onRecycleChange = useCallback((id: string, value: number | boolean | Date) => {
    const recycleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_RECYCLE))
    if (!recycleAvailable) {
      return
    }
    recycle$.current?.next({ postId: id, value })
  }, [dispatch])

  const onShuffle = useCallback(() => {
    const shuffleAvailable = dispatch(checkFeatureAvailability(FEATURE_POST_SHUFFLE))
    if (!shuffleAvailable) {
      return
    }
    shuffle$.current?.next(selectionRef.current as any)
  }, [dispatch])

  const togglePostSelected = useCallback((id: string) => {
    setSelectedPostsIds(current => {
      if (current === 'all') {
        return postsRef.current.reduce((selection: Record<string, boolean>, post) => {
          selection[post.id] = post.id !== id
          return selection
        }, {})
      }
      return { ...current, [id]: !current[id] }
    })
  }, [])

  const onCopyPosts = useCallback(() => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      if (selectionRef.current?.all) {
        setPostsToCopy(postsRef.current)
      } else if (selectionRef.current?.postIds) {
        setPostsToCopy(postsRef.current.filter(p => selectionRef.current?.postIds.includes(p.id)))
      }
    }
  }, [dispatch])

  const closePostMediaPreviewDialog = useCallback(() => {
    setPostInMediaPreviewDialog(null)
  }, [])

  const deleteSelectedPosts = useCallback(() => {
    delete$.current?.next(selectionRef.current as any)
  }, [])

  const deletePostsConfirmMessage = selectedPostsIds === 'all'
    ? intl.formatMessage({ id: 'post.delete-all-confirm' }, { count: totalPostsCount, type: 'pending' })
    : undefined

  const onEdit = useCallback((post: IPlannedPost) => {
    dispatch(resetComposer())
    dispatch(mergeState(getDerivedStateFromPost(post, 'planned', 'pending')))
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: location.pathname, background: location } })
  }, [dispatch, location, navigate])

  const onFilter = useCallback((query: string, types: ContentType[], searchInSummary: boolean) => {
    setSearch({ query, types, searchInSummary })
    setSelectedPostsIds({})
  }, [])

  const previewSelection = useMemo(() => {
    const selectedPostIndex = postInPreview
      ? nonEmptyPosts.findIndex((p: any) => p.id === postInPreview.id)
      : -1
    let hasNext = false
    let hasPrev = false

    if (selectedPostIndex !== -1) {
      hasNext = selectedPostIndex < nonEmptyPosts.length - 1
      hasPrev = selectedPostIndex >= 1
    }
    return {
      selectedPostIndex,
      hasNext,
      hasPrev
    }
  }, [postInPreview, nonEmptyPosts])

  const selectNextPostForPreview = useCallback(() => {
    if (previewSelection.hasNext) {
      const next = nonEmptyPosts[previewSelection.selectedPostIndex + 1]
      setPostInPreview(next)
    }
  }, [previewSelection, nonEmptyPosts])

  const selectPrevPostForPreview = useCallback(() => {
    if (previewSelection.hasPrev) {
      const prev = nonEmptyPosts[previewSelection.selectedPostIndex - 1]
      setPostInPreview(prev)
    }
  }, [previewSelection, nonEmptyPosts])

  const deletePost = useCallback((post: IPlannedPost) => {
    onDeletePost(post.id)
  }, [onDeletePost])

  const onCopyPost = useCallback((post: IPlannedPost) => {
    const copyAllowed = dispatch(checkFeatureAvailability(FEATURE_POST_COPY))
    if (copyAllowed) {
      setPostsToCopy([post])
    }
  }, [dispatch])

  const closeCopyPostsPopup = useCallback(() => {
    setPostsToCopy([])
    setSelectedPostsIds({})
  }, [])

  const visiblePosts = useMemo(() => {
    if (emptySlotsVisible) {
      return posts
    }
    return posts.filter(p => !p.isEmpty)
  }, [posts, emptySlotsVisible])

  const onShareNow = useCallback((post: IPlannedPost) => {
    share$.current?.next({ all: false, postIds: [post.id], ppids: [post.ppPageId] })
  }, [])

  const showEmptyPostsMessage = !loading && selectedProfilesIds.length > 0 && visiblePosts.length === 0

  const renderPosts = useCallback((data: Record<string, PostGroupData>) => {
    const allPosts: IPlannedPost[] = Object.values(data).reduce((posts: IPlannedPost[], group: PostGroupData) => {
      posts.push(...group.posts)
      return posts
    }, [])

    return (
      <div className={styles['pending-timeline']}>
        {allPosts.map(post => (
          <PlannedPost
            key={post.id}
            selected={selectedPostsIds === 'all' || Boolean(selectedPostsIds[post.id])}
            post={post}
            onPostClick={onPostClick}
            onEdit={onEdit}
            onCopy={onCopyPost}
            onDelete={onDeletePost}
            onRecycleChange={onRecycleChange}
            onToggleSelected={togglePostSelected}
            onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
            onShareNow={onShareNow}
          />
        ))}
      </div>
    )
  }, [onDeletePost, onEdit, onPostClick, onRecycleChange, selectedPostsIds, togglePostSelected, onShareNow, onCopyPost])

  const selectedPostsCount = useMemo(() => {
    return selectedPostsIds !== 'all'
      ? Object.values(selectedPostsIds).filter(Boolean).length
      : totalPostsCount || 0
  }, [totalPostsCount, selectedPostsIds])

  // EXPL: memoize so a fresh object ref doesn't break the memoized PlannedPostsList
  // (and grid) on every parent render. Shared — both views use identical props.
  const bulkActionsProps = useMemo<BulkPostActionsProps>(() => ({
    hidePostActions: false,
    withDelete: true,
    withCopy: true,
    allSelected: selectedPostsIds === 'all',
    disabled: selectedPostsCount === 0,
    selectedCount: selectedPostsCount,
    deleteAllConfirmMessage: deletePostsConfirmMessage,
    skipDeleteConfirm: true, // undo snackbar available (onRestored passed to bulkDeletePosts)
    onToggleAllSelected: toggleAllPostsSelected,
    onBulkDeletePosts: deleteSelectedPosts,
    onCopy: onCopyPosts,
    onShuffle
  }), [
    selectedPostsIds, selectedPostsCount, deletePostsConfirmMessage,
    toggleAllPostsSelected, deleteSelectedPosts, onCopyPosts, onShuffle
  ])

  return (
    <div className={styles.page}>
      <PublishingTopBar
        withViewPicker
        withPreviewToggle
        withPostsViewToggle
        postsView="posts/planned/queued/pending"
        activeTypes={search.types}
        showPreview={withPreview}
        emptySlotsVisible={emptySlotsVisible}
        totalCount={totalPostsCount}
        children={<HelpToggleButton />}
        hideViewOptions={['calendar']}
        onTogglePreview={onTogglePreview}
        onFilter={onFilter}
      />
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {loadingOverlayActive && (
            <div className={styles['loading-overlay']}>
              <SimpleLoader small />
            </div>
          )}
          {activeView === 'list' && (
            <PlannedPostsList
              posts={visiblePosts}
              selectedPosts={selectedPostsIds}
              activePreviewPostId={postInPreview?.id}
              bulkActionsProps={bulkActionsProps}
              renderPosts={renderPosts}
              onLoadNextPage={loadNextPage}
              onTogglePostSelected={togglePostSelected}
              onDeletePost={onDeletePost}
              onEditPost={onEdit}
              onCopyPost={onCopyPost}
              onMediaPreviewBtnClick={setPostInMediaPreviewDialog}
              onRecycleChange={onRecycleChange}
              onPostClick={onPostClick}
              onShareNow={onShareNow}
            />
          )}
          {activeView === 'grid' && !showEmptyPostsMessage && (
            <PostsGridView
              posts={visiblePosts}
              selectedPosts={selectedPostsIds}
              bulkActionsProps={bulkActionsProps}
              withDragAndDrop={false}
              onLoadNextPage={loadNextPage}
              onPostClick={onPostClick}
              onTogglePostSelected={togglePostSelected}
              onDeletePost={onDeletePost}
              onEditPost={onEdit}
              onCopyPost={onCopyPost}
              onShareNow={onShareNow}
            />
          )}
          {!loading && selectedProfilesIds.length === 0 && (
            <PostErrorView title={ERROR_NO_PROFILES_TITLE} subtitle={ERROR_NO_PROFILES_SUBTITLE} />
          )}
          {!loading && selectedProfilesIds.length > 0 && posts.length === 0 && (
            <EmptyView
              icon={mdiFeather}
              title="No pending posts"
              subtitle={(
                <div>
                  All your <NavLink to="/posts/planned/queued">queued posts</NavLink>
                  &nbsp;have active <NavLink to="/plans">time slots</NavLink>
                </div>
              )}
              backdrop
            />
          )}
          {loading && !loadingOverlayActive && <SimpleLoader small />}
        </div>
        <div
          className={`${styles['preview-box']} ${withPreview ? styles.visible : ''}`}
        >
          {postInPreview && (
            <PostPreviewWithInfo
              post={postInPreview}
              hasNextPost={previewSelection.hasNext}
              hasPreviousPost={previewSelection.hasPrev}
              className={styles.preview}
              onClose={onTogglePreview}
              onSelectNext={selectNextPostForPreview}
              onSelectPrevious={selectPrevPostForPreview}
              skipDeleteConfirm
              onDelete={deletePost}
              onEdit={onEdit}
              onCopy={onCopyPost}
              onRecycleChange={onRecycleChange}
              onPostUpdated={refreshPosts}
            />
          )}
        </div>
        <PostFilesDialog
          active={Boolean(postInMediaPreviewDialog)}
          images={postInMediaPreviewDialog?.images || []}
          onClose={closePostMediaPreviewDialog}
        />
        <CopyPostsPopup
          open={postsToCopy.length > 0}
          posts={postsToCopy}
          allSelected={Boolean(selectionRef.current?.all)}
          query={search.query}
          types={search.types}
          postType="pending"
          onClose={closeCopyPostsPopup}
          onCopyStarted={closeCopyPostsPopup}
          onCopyFinished={refreshPosts}
        />
      </div>
    </div>
  )
}

export default PendingPostsManager
